/**
 * Drills API - Client-Side Service
 * 
 * Drills are shared resources. All users can read, only admins can write.
 * All operations go through server-side Express API.
 * Client-side code MUST NOT access the database directly.
 */
import type { Drill, Category } from '../types/drill-types';
import { apiFetch, pollEndpoint } from '@/lib/api-client';

// Response types
interface CreateDrillResponse {
  success: boolean;
  drillId: string;
  message: string;
}

interface CreateCategoryResponse {
  success: boolean;
  categoryId: string;
  message: string;
}

interface UpdateResponse {
  success: boolean;
  message: string;
}

interface BulkUpdateResponse {
  success: boolean;
  message: string;
  count: number;
}

interface ImportResponse {
  success: boolean;
  imported: number;
  updated: number;
  errors: string[];
  message: string;
}

// ============ DRILL OPERATIONS ============

/**
 * Get all drills from the shared library
 */
export async function getDrills(): Promise<Drill[]> {
  return apiFetch<Drill[]>('/drills');
}

/**
 * Get a single drill by ID
 */
export async function getDrill(drillId: string): Promise<Drill | null> {
  try {
    return await apiFetch<Drill>(`/drills/${drillId}`);
  } catch (error: any) {
    if (error.message?.includes('not-found') || error.message?.includes('404')) {
      return null;
    }
    throw error;
  }
}

/**
 * Create a new drill (ADMIN ONLY)
 */
export async function createDrill(drillData: Omit<Drill, 'id'>): Promise<string> {
  const response = await apiFetch<CreateDrillResponse>('/drills', {
    method: 'POST',
    body: JSON.stringify(drillData),
  });
  return response.drillId;
}

/**
 * Update a drill (ADMIN ONLY)
 */
export async function updateDrill(
  drillId: string,
  updates: Partial<Drill>
): Promise<void> {
  await apiFetch<UpdateResponse>(`/drills/${drillId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete a drill (ADMIN ONLY)
 */
export async function deleteDrill(drillId: string): Promise<void> {
  await apiFetch<UpdateResponse>(`/drills/${drillId}`, {
    method: 'DELETE',
  });
}

/**
 * Bulk update drills (ADMIN ONLY)
 */
export async function bulkUpdateDrills(
  updates: { id: string; data: Partial<Drill> }[]
): Promise<number> {
  const response = await apiFetch<BulkUpdateResponse>('/drills/bulk-update', {
    method: 'POST',
    body: JSON.stringify({ updates }),
  });
  return response.count;
}

/**
 * Bulk delete drills (ADMIN ONLY)
 */
export async function bulkDeleteDrills(drillIds: string[]): Promise<number> {
  const response = await apiFetch<BulkUpdateResponse>('/drills/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ drillIds }),
  });
  return response.count;
}

/**
 * Delete ALL drills (ADMIN ONLY - DANGEROUS)
 */
export async function deleteAllDrills(): Promise<number> {
  const response = await apiFetch<BulkUpdateResponse>('/drills/delete-all', {
    method: 'POST',
    body: JSON.stringify({ confirmDelete: true }),
  });
  return response.count;
}

/**
 * Import drills (ADMIN ONLY)
 */
export async function importDrills(
  drills: Omit<Drill, 'id'>[],
  overwriteExisting: boolean = true
): Promise<ImportResponse> {
  return apiFetch<ImportResponse>('/drills/import', {
    method: 'POST',
    body: JSON.stringify({ drills, overwriteExisting }),
  });
}

// ============ CATEGORY OPERATIONS ============

/**
 * Get all categories
 */
export async function getCategories(): Promise<Category[]> {
  return apiFetch<Category[]>('/categories');
}

/**
 * Create a category (ADMIN ONLY)
 */
export async function createCategory(categoryData: Omit<Category, 'id'>): Promise<string> {
  const response = await apiFetch<CreateCategoryResponse>('/categories', {
    method: 'POST',
    body: JSON.stringify(categoryData),
  });
  return response.categoryId;
}

/**
 * Update a category (ADMIN ONLY)
 */
export async function updateCategory(
  categoryId: string,
  updates: Partial<Category>
): Promise<void> {
  await apiFetch<UpdateResponse>(`/categories/${categoryId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete a category (ADMIN ONLY)
 */
export async function deleteCategory(categoryId: string): Promise<void> {
  await apiFetch<UpdateResponse>(`/categories/${categoryId}`, {
    method: 'DELETE',
  });
}

/**
 * Subscribe to drills with polling for real-time updates
 * Returns cleanup function to stop polling
 */
export function subscribeToDrills(
  callback: (drills: Drill[]) => void,
  intervalMs: number = 5000,
  onError?: (error: Error) => void
): () => void {
  return pollEndpoint<Drill[]>('/drills', callback, intervalMs, onError);
}

/**
 * Subscribe to categories with polling for real-time updates
 * Returns cleanup function to stop polling
 */
export function subscribeToCategories(
  callback: (categories: Category[]) => void,
  intervalMs: number = 5000,
  onError?: (error: Error) => void
): () => void {
  return pollEndpoint<Category[]>('/categories', callback, intervalMs, onError);
}
